import type { Tarea, EstadoTarea, PrioridadTarea, TipoCobro, TipoTarea, EstadoAprobacion, TareaEvidencia, Factura, ConfigTarifaCliente } from '@/lib/types'
import { useSupabase } from '@/hooks/useSupabase'
import { useApi } from '@/hooks/useApi'
import { showToast } from '@/components/layout/Toast'
import { useState, useEffect } from 'react'
import { r2, formatCurrency } from '@/lib/utils'

import { IconEdit, IconPlus, IconTrash, IconUpload, IconPaperclip, IconFile, IconImage, IconDollar } from '@/components/icons/Icons'

interface TareaModalProps {
  tarea: Tarea | null        // null = create mode
  proyectoId: number
  clienteId: number
  tarifaCliente: ConfigTarifaCliente | null
  facturas: Factura[]
  onClose: () => void
  onSaved: () => void
  onDeleted?: () => void
}

const ESTADOS: { value: EstadoTarea; label: string }[] = [
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'en_progreso', label: 'En Progreso' },
  { value: 'completada', label: 'Completada' },
  { value: 'bloqueada', label: 'Bloqueada' },
]

const PRIORIDADES: { value: PrioridadTarea; label: string }[] = [
  { value: 'baja', label: 'Baja' },
  { value: 'media', label: 'Media' },
  { value: 'alta', label: 'Alta' },
  { value: 'urgente', label: 'Urgente' },
]

const TIPOS_COBRO_LABELS: { value: TipoCobro; label: string }[] = [
  { value: 'sin_costo', label: 'Sin costo' },
  { value: 'monto_fijo', label: 'Monto fijo' },
  { value: 'por_horas', label: 'Por horas' },
]

const TIPOS_TAREA_LABELS: { value: TipoTarea; label: string; color: string; bg: string }[] = [
  { value: 'tarea', label: 'Tarea', color: 'var(--info)', bg: 'color-mix(in srgb, var(--info) 12%, transparent)' },
  { value: 'bug', label: 'Bug', color: 'var(--danger)', bg: 'color-mix(in srgb, var(--danger) 12%, transparent)' },
  { value: 'mejora', label: 'Mejora', color: 'var(--success)', bg: 'color-mix(in srgb, var(--success) 12%, transparent)' },
]

const APROBACION_LABELS: { value: EstadoAprobacion; label: string }[] = [
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'enviada', label: 'Enviada' },
  { value: 'aprobada', label: 'Aprobada' },
  { value: 'rechazada', label: 'Rechazada' },
]

export default function TareaModal({ tarea, proyectoId, clienteId: _clienteId, tarifaCliente, facturas, onClose, onSaved, onDeleted }: TareaModalProps) {
  const db = useSupabase()
  const api = useApi()
  const isEdit = tarea !== null

  const [form, setForm] = useState({
    titulo: '',
    descripcion: '',
    estado: 'pendiente' as EstadoTarea,
    prioridad: 'media' as PrioridadTarea,
    tipo: 'tarea' as TipoTarea,
    fecha_limite: '',
    notas: '',
    etiquetas: [] as string[],
    tipo_cobro: 'por_horas' as TipoCobro,
    monto: 0,
    horas_estimadas: 0,
    horas_reales: 0,
    estado_aprobacion: 'pendiente' as EstadoAprobacion,
    factura_id: null as number | null,
  })
  const [tagInput, setTagInput] = useState('')
  const [saving, setSaving] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  // Evidencia state
  const [evidencias, setEvidencias] = useState<TareaEvidencia[]>([])
  /**
   * Archivos elegidos ANTES de que la tarea exista. Se suben apenas se crea.
   * Sin esto habia que guardar, cerrar y volver a abrir para adjuntar algo,
   * que es la razon por la que en 500 tareas habia 3 adjuntos.
   */
  const [pendientes, setPendientes] = useState<File[]>([])
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    if (tarea) {
      setForm({
        titulo: tarea.titulo,
        descripcion: tarea.descripcion ?? '',
        estado: tarea.estado,
        prioridad: tarea.prioridad,
        tipo: tarea.tipo ?? 'tarea',
        fecha_limite: tarea.fecha_limite ?? '',
        notas: tarea.notas ?? '',
        etiquetas: tarea.etiquetas ?? [],
        tipo_cobro: tarea.tipo_cobro ?? 'por_horas',
        monto: tarea.monto ?? 0,
        horas_estimadas: tarea.horas_estimadas ?? 0,
        horas_reales: tarea.horas_reales ?? 0,
        estado_aprobacion: tarea.estado_aprobacion ?? 'pendiente',
        factura_id: tarea.factura_id ?? null,
      })
      // Load evidencias
      loadEvidencias(tarea.id)
    }
  }, [tarea])

  async function loadEvidencias(tareaId: number) {
    try {
      const all = await db.getAll<TareaEvidencia>('tarea_evidencias')
      setEvidencias(all.filter(e => e.tarea_id === tareaId))
    } catch { /* silently fail */ }
  }

  // Auto-calc monto for hourly tasks
  const montoCalculado = form.tipo_cobro === 'por_horas' && tarifaCliente
    ? r2(form.horas_estimadas * tarifaCliente.tarifa_hora)
    : form.monto

  const moneda = tarifaCliente?.moneda ?? 'USD'

  function setField(field: string, value: string | number | null) {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  function addTag() {
    const tag = tagInput.trim()
    if (tag && !form.etiquetas.includes(tag)) {
      setForm(prev => ({ ...prev, etiquetas: [...prev.etiquetas, tag] }))
      setTagInput('')
    }
  }

  function removeTag(tag: string) {
    setForm(prev => ({ ...prev, etiquetas: prev.etiquetas.filter(t => t !== tag) }))
  }

  // ─── Adjuntos ───

  const MAX_ADJUNTO = 25 * 1024 * 1024

  /** Sube uno. Devuelve la fila creada, o null si fallo. */
  async function subirArchivo(tareaId: number, file: File): Promise<TareaEvidencia | null> {
    const fd = new FormData()
    fd.append('file', file)
    // Un archivo que se adjunta mientras la tarea esta abierta es un INSUMO.
    // La evidencia (la prueba de que quedo hecho) se marca aparte.
    fd.append('categoria', form.estado === 'completada' ? 'evidencia' : 'documento')
    try {
      return await api.upload<TareaEvidencia>(`/api/tareas/${tareaId}/adjuntos/upload`, fd)
    } catch {
      showToast(`No se pudo subir ${file.name}`, 'error')
      return null
    }
  }

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? [])
    e.target.value = ''
    if (!files.length) return

    const grandes = files.filter(f => f.size > MAX_ADJUNTO)
    if (grandes.length) {
      showToast(`${grandes[0].name} supera los 25 MB`, 'error')
      return
    }

    // Tarea nueva: todavia no hay a quien colgarselos, se encolan.
    if (!tarea) {
      setPendientes(prev => [...prev, ...files])
      return
    }

    setUploading(true)
    try {
      for (const f of files) await subirArchivo(tarea.id, f)
      await loadEvidencias(tarea.id)
    } finally {
      setUploading(false)
    }
  }

  async function removeEvidencia(ev: TareaEvidencia) {
    try {
      // La ruta propia borra tambien el objeto en R2. El borrado viejo solo
      // quitaba la fila y dejaba el archivo colgado para siempre.
      await api.del(`/api/tareas/adjuntos/${ev.id}`)
      setEvidencias(prev => prev.filter(e => e.id !== ev.id))
      showToast('Adjunto eliminado', 'success')
    } catch {
      showToast('Error al eliminar', 'error')
    }
  }

  async function handleSave() {
    if (!form.titulo.trim()) return
    setSaving(true)
    try {
      const now = new Date().toISOString()
      const finalMonto = form.tipo_cobro === 'sin_costo' ? 0 : montoCalculado
      const record: Record<string, unknown> = {
        proyecto_id: proyectoId,
        titulo: form.titulo.trim(),
        descripcion: form.descripcion.trim() || null,
        estado: form.estado,
        prioridad: form.prioridad,
        fecha_limite: form.fecha_limite || null,
        etiquetas: form.etiquetas,
        notas: form.notas.trim() || null,
        fecha_completada: form.estado === 'completada' ? now : null,
        tipo: form.tipo,
        tipo_cobro: form.tipo_cobro,
        monto: finalMonto,
        horas_estimadas: form.horas_estimadas,
        horas_reales: form.horas_reales,
        estado_aprobacion: form.tipo_cobro === 'sin_costo' ? 'pendiente' : form.estado_aprobacion,
        factura_id: form.factura_id || null,
      }
      if (isEdit) {
        record.id = tarea!.id
        await db.update('tareas', record)
      } else {
        const nuevoId = await db.add('tareas', record)
        // Los archivos que se eligieron antes de que la tarea existiera. Si
        // alguno falla no se tumba el guardado: la tarea ya esta creada y
        // perderla por un adjunto seria peor.
        for (const f of pendientes) await subirArchivo(nuevoId, f)
        if (pendientes.length) showToast(`${pendientes.length} archivo(s) adjuntado(s)`, 'success')
      }
      onSaved()
    } catch (err) {
      console.error('Error saving task:', err)
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete() {
    if (!tarea) return
    try {
      await db.remove('tareas', tarea.id)
      onDeleted?.()
    } catch (err) {
      console.error('Error deleting task:', err)
    }
  }

  const hasCost = form.tipo_cobro !== 'sin_costo'

  return (
    <div className="modal-overlay" id="tarea-modal-overlay">
      <div className="modal" style={{ maxWidth: 640, display: 'flex', flexDirection: 'column', maxHeight: '85vh' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{isEdit ? <><IconEdit size={18} /> Editar Tarea</> : <><IconPlus size={18} /> Nueva Tarea</>}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body" style={{ overflowY: 'auto', flex: 1, paddingBottom: 8 }}>
          {/* Título */}
          <div className="form-group">
            <label className="form-label">Título *</label>
            <input
              className="form-input"
              id="tarea-titulo"
              value={form.titulo}
              onChange={e => setField('titulo', e.target.value)}
              placeholder="Ej: Configurar ambiente de staging"
              autoFocus
              required
            />
          </div>

          {/* Tipo de tarea */}
          <div className="form-group">
            <label className="form-label">Tipo</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {TIPOS_TAREA_LABELS.map(tt => (
                <button
                  key={tt.value}
                  type="button"
                  style={{
                    padding: '4px 14px', borderRadius: 20, fontSize: '0.82rem', fontWeight: 600,
                    cursor: 'pointer', border: '1.5px solid',
                    background: form.tipo === tt.value ? tt.bg : 'transparent',
                    color: form.tipo === tt.value ? tt.color : 'var(--text-muted)',
                    borderColor: form.tipo === tt.value ? tt.color : 'var(--border)',
                    transition: 'all 0.15s ease',
                  }}
                  onClick={() => {
                    setField('tipo', tt.value)
                    // Auto-default: bugs default to sin_costo, tareas/mejoras to por_horas
                    if (tt.value === 'bug' && form.tipo_cobro === 'por_horas') {
                      setField('tipo_cobro', 'sin_costo')
                    }
                    if (tt.value !== 'bug' && form.tipo_cobro === 'sin_costo') {
                      setField('tipo_cobro', 'por_horas')
                    }
                  }}
                >
                  {tt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Estado + Prioridad */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Estado</label>
              <select
                className="form-select"
                id="tarea-estado"
                value={form.estado}
                onChange={e => setField('estado', e.target.value)}
              >
                {ESTADOS.map(e => (
                  <option key={e.value} value={e.value}>{e.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Prioridad</label>
              <select
                className="form-select"
                id="tarea-prioridad"
                value={form.prioridad}
                onChange={e => setField('prioridad', e.target.value)}
              >
                {PRIORIDADES.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Fecha límite */}
          <div className="form-group">
            <label className="form-label">Fecha límite</label>
            <input
              className="form-input"
              id="tarea-fecha-limite"
              type="date"
              value={form.fecha_limite}
              onChange={e => setField('fecha_limite', e.target.value)}
            />
          </div>

          {/* ─── Facturación Section ─── */}
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '1rem', marginBottom: '1rem', background: 'color-mix(in srgb, var(--rol-principal) 3%, transparent)' }}>
            <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: 'var(--accent-primary)', textTransform: 'uppercase', letterSpacing: 1 }}>
              <IconDollar size={14} /> Facturación
            </h4>

            {/* Tipo de cobro */}
            <div className="form-group">
              <label className="form-label">Tipo de cobro</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {TIPOS_COBRO_LABELS.map(tc => (
                  <button
                    key={tc.value}
                    type="button"
                    className={`tag-chip ${form.tipo_cobro === tc.value ? 'active' : ''}`}
                    onClick={() => setField('tipo_cobro', tc.value)}
                    style={{ cursor: 'pointer' }}
                  >
                    {tc.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Monto fijo */}
            {form.tipo_cobro === 'monto_fijo' && (
              <div className="form-group">
                <label className="form-label">Monto ({moneda})</label>
                <input
                  className="form-input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.monto}
                  onChange={e => setField('monto', parseFloat(e.target.value) || 0)}
                  placeholder="0.00"
                  style={{ maxWidth: 200 }}
                />
              </div>
            )}

            {/* Por horas — tarifa */}
            {form.tipo_cobro === 'por_horas' && (
              <div>
                {tarifaCliente ? (
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', padding: '6px 10px', background: 'var(--bg-tertiary)', borderRadius: 6, display: 'flex', justifyContent: 'space-between' }}>
                    <span>Tarifa: {formatCurrency(tarifaCliente.tarifa_hora, moneda)}/h</span>
                    <span style={{ fontWeight: 700, color: 'var(--accent-primary)' }}>
                      Total: {formatCurrency(montoCalculado, moneda)}
                    </span>
                  </div>
                ) : (
                  <div style={{ fontSize: '0.82rem', color: 'var(--warning)', padding: '6px 10px', background: 'color-mix(in srgb, var(--warning) 10%, transparent)', borderRadius: 6 }}>
                    Sin tarifa configurada para este cliente. Configúrala en el panel de Clientes.
                  </div>
                )}
              </div>
            )}

            {/* Horas — siempre visible */}
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Horas estimadas</label>
                <input
                  className="form-input"
                  type="number"
                  step="0.5"
                  min="0"
                  value={form.horas_estimadas}
                  onChange={e => setField('horas_estimadas', parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  style={{ maxWidth: 160 }}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Horas reales</label>
                <input
                  className="form-input"
                  type="number"
                  step="0.5"
                  min="0"
                  value={form.horas_reales}
                  onChange={e => setField('horas_reales', parseFloat(e.target.value) || 0)}
                  placeholder="0"
                  style={{ maxWidth: 160 }}
                />
              </div>
            </div>

            {/* Estado de aprobación (solo si tiene costo) */}
            {hasCost && (
              <div className="form-row" style={{ marginTop: 12 }}>
                <div className="form-group">
                  <label className="form-label">Estado de aprobación</label>
                  <select
                    className="form-select"
                    value={form.estado_aprobacion}
                    onChange={e => setField('estado_aprobacion', e.target.value)}
                  >
                    {APROBACION_LABELS.map(a => (
                      <option key={a.value} value={a.value}>{a.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Factura vinculada</label>
                  <select
                    className="form-select"
                    value={form.factura_id ?? ''}
                    onChange={e => setField('factura_id', e.target.value ? Number(e.target.value) : null)}
                  >
                    <option value="">Sin factura</option>
                    {facturas.map(f => (
                      <option key={f.id} value={f.id}>{f.numero} — {formatCurrency(f.total, f.moneda)}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Descripción */}
          <div className="form-group">
            <label className="form-label">Descripción</label>
            <textarea
              className="form-textarea"
              id="tarea-descripcion"
              value={form.descripcion}
              onChange={e => setField('descripcion', e.target.value)}
              placeholder="Descripción detallada de la tarea..."
            />
          </div>

          {/* Etiquetas */}
          <div className="form-group">
            <label className="form-label">Etiquetas</label>
            <div className="tags-input-container">
              <div className="tags-chips-list">
                {form.etiquetas.map(tag => (
                  <span key={tag} className="tag-chip editable">
                    {tag}
                    <button onClick={() => removeTag(tag)} title="Quitar">&times;</button>
                  </span>
                ))}
              </div>
              <input
                className="form-input"
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag() } }}
                placeholder="Agregar etiqueta..."
                style={{ flex: 1, minWidth: 120 }}
              />
            </div>
          </div>

          {/* ─── Adjuntos ─── */}
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '1rem', marginBottom: '1rem' }}>
            <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
              <IconPaperclip size={14} /> Adjuntos ({evidencias.length + pendientes.length})
            </h4>

            <div style={{ marginBottom: '0.75rem' }}>
              <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {uploading ? (
                  <><div className="auth-spinner" style={{ width: 14, height: 14 }} /> Subiendo...</>
                ) : (
                  <><IconUpload size={14} /> Adjuntar archivo</>
                )}
                {/* Sin `accept`: cualquier tipo. El filtro viejo (img/MP4/PDF)
                    dejaba fuera justo lo que hacia falta — el Word del
                    contrato, la planilla que manda el cliente. */}
                <input type="file" multiple onChange={handleUpload} style={{ display: 'none' }} disabled={uploading} />
              </label>
              <span style={{ marginLeft: 8, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                Cualquier formato, hasta 25 MB
              </span>
            </div>

            {/* Lo elegido antes de que la tarea exista: se sube al guardar. */}
            {pendientes.length > 0 && (
              <div style={{ marginBottom: 8, fontSize: '0.78rem', color: 'var(--text-muted)', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {pendientes.map((f, i) => (
                  <span key={i} className="badge badge-tarea-pendiente" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <IconFile size={11} /> {f.name}
                    <button
                      type="button"
                      onClick={() => setPendientes(prev => prev.filter((_, j) => j !== i))}
                      style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, lineHeight: 1 }}
                      title="Quitar"
                    >x</button>
                  </span>
                ))}
                <span>— se suben al guardar</span>
              </div>
            )}

            {evidencias.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8 }}>
                {evidencias.map(ev => (
                  <div key={ev.id} style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)', background: 'var(--bg-tertiary)' }}>
                    {ev.tipo?.startsWith('image/') ? (
                      <a href={ev.url} target="_blank" rel="noopener noreferrer">
                        <img src={ev.url} alt={ev.nombre} style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }} />
                      </a>
                    ) : (
                      <a href={ev.url} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 90 }}>
                        {ev.tipo?.startsWith('video/')
                          ? <IconImage size={32} style={{ color: 'var(--text-muted)' }} />
                          : <IconFile size={32} style={{ color: 'var(--text-muted)' }} />}
                      </a>
                    )}
                    <div style={{ padding: '4px 6px', fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {ev.nombre}
                    </div>
                    <button
                      onClick={() => removeEvidencia(ev)}
                      style={{ position: 'absolute', top: 2, right: 2, background: 'rgba(0,0,0,0.6)', color: 'var(--text-on-primary)', border: 'none', borderRadius: '50%', width: 20, height: 20, fontSize: '0.7rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      title="Eliminar"
                    >x</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notas */}
          <div className="form-group">
            <label className="form-label">Notas</label>
            <textarea
              className="form-textarea"
              id="tarea-notas"
              value={form.notas}
              onChange={e => setField('notas', e.target.value)}
              placeholder="Notas internas..."
              style={{ minHeight: 60 }}
            />
          </div>
        </div>

        {/* Sticky footer actions */}
        <div className="modal-actions" style={{ borderTop: '1px solid var(--border)', padding: '12px 24px', background: 'var(--bg-secondary)', borderRadius: '0 0 12px 12px', flexShrink: 0 }}>
          {isEdit && (
            confirmDelete ? (
              <div style={{ display: 'flex', gap: 8, marginRight: 'auto' }}>
                <button className="btn btn-danger btn-sm" onClick={handleDelete}>Confirmar Eliminar</button>
                <button className="btn btn-secondary btn-sm" onClick={() => setConfirmDelete(false)}>No</button>
              </div>
            ) : (
              <button
                className="btn btn-secondary btn-sm"
                style={{ marginRight: 'auto', color: 'var(--danger)' }}
                onClick={() => setConfirmDelete(true)}
              >
                <IconTrash size={13} /> Eliminar
              </button>
            )
          )}
          <button className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          <button
            className="btn btn-primary"
            id="tarea-save-btn"
            onClick={handleSave}
            disabled={saving || !form.titulo.trim()}
          >
            {saving ? 'Guardando...' : isEdit ? 'Actualizar' : 'Crear Tarea'}
          </button>
        </div>
      </div>
    </div>
  )
}
